/**
 * useStudentLiveScreen — l'écran du live de l'élève, prêt à poser : le moteur (`useStudentLive`),
 * l'activité composée (`useStudentActivityView` + `<StudentActivityBody>`), et ce qu'ils donnent en
 * props de `<StudentLivePage>`.
 *
 * Ce qu'il tient lui-même, parce que c'est de l'écran :
 *  - **l'activité ouverte** : celle que le tuteur pousse ou que l'élève choisit (le moteur), sinon la
 *    suggestion de Sam ou la plus récente des activités à faire (`pickLiveActivity`), rangée dans
 *    `data.currentActivity` comme aujourd'hui (elle survit à l'app tuée) ; à la fin d'une activité,
 *    la suivante ; retirée par le tuteur, elle se ferme sur `deleteTodo`, ou sur la liste relue qui ne la
 *    porte plus ;
 *  - **les entrées du live de l'activité** (`ActivityLiveInputs`) : la séance, la pause du tuteur (et
 *    le Dialogue d'un cours partagé, comme `TimerActivityStep`), les annonces au tuteur
 *    (`studentWorkUpdated`, `activityFinalized`), l'oral en cours, l'attribut `activity_id` ;
 *  - **ce que le tuteur lit de l'écran de l'élève** : `activity_id`, l'activité OUVERTE (posée par
 *    l'activité), et `activity_on_screen`, « 1 » quand elle est DEVANT lui (ce que la page dit ; une activité
 *    faite relue la cache), « 0 » sinon. La forme et sa compatibilité : `ports/TRANSITION.md`, lives des 23 et
 *    24/10 ;
 *  - **« Nouvelle activité de ton masteur · Voir »** : l'activité ajoutée sans être affichée, dès que la
 *    liste relue la porte ; touchée, elle s'ouvre comme un choix dans « Mes activités » ;
 *  - **Sam touché** : « Tu veux parler à Sam ? » d'abord (Wajih, 25/10) ;
 *  - **le quiz de prérequis en live** : le tuteur prévenu à la fin (`agentConversationCompleted`),
 *    `ongoing_checkpoint` pendant le quiz, `finish tutor_interruption` quand le tuteur pousse une
 *    autre activité — comme l'ancien `SamCheckpointOverlay` ;
 *  - les feuilles : le chat, « Un problème ? », « Mes activités » (avec les activités faites de
 *    l'onglet Activités), « Veux-tu vraiment quitter ? », la note en partant ;
 *  - **la relecture d'une activité faite** (touchée dans « Mes activités ») : elle prend la place de
 *    l'activité dans le volet du live, comme l'ancien live la posait à la place de l'activité courante,
 *    sans route ni écran legacy, et sans rien dire à la salle (`activity_id` reste celle de l'activité
 *    du live : l'élève y revient par le retour) ;
 *  - le bandeau « Installer l'appli » du web mobile (`useLiveAppBanner`) ;
 *  - le mode autonome d'un cours de groupe (`useAutoMode`) : à la fin d'une activité, en Concentré
 *    et la liste vide, Sam demande comment ça s'est passé et propose la suite ;
 *  - le partage de l'écran sur le web, au-delà de 700 px de fenêtre comme le bouton de l'ancienne
 *    barre (`ControlBarM.web`) ;
 *  - les sons (`playLiveSound`) et la sortie (`useLiveClosing`) : le message de clôture, puis, hors du
 *    live, le quiz de fin de séance et le rapport (`live-end`).
 */
import { useFocusEffect } from "@react-navigation/native"
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { Platform, useWindowDimensions } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { API_URL } from "../../../../../../data/constants/apiConstants"
import { setCurrentActivity, setOngoingActivity } from "../../../../../../data/redux/actions/activity"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { supportsNoShowRecovery } from "../../../../../../data/utility/liveSessionType"
import { type ApiCheckpointConversation } from "../../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import { useMediaPermissions } from "../../../../../../engine/live/room/useMediaPermissions"
import { useNoiseFilter } from "../../../../../../engine/live/room/useNoiseFilter"
import {
    CHECKPOINT_CONTEXT_ATTRIBUTE,
    PREREQUISITE_QUIZ_CONTEXT,
} from "../../../../../../engine/live/room/checkpointContext"
import { useCameraFlip } from "../../../../../../engine/live/room/useCameraFlip"
import { useRoomDevices } from "../../../../../../engine/live/room/useRoomDevices"
import { useScreenShareToggle } from "../../../../../../engine/live/room/useScreenShareToggle"
import { autoModeOpens } from "../../../../../../engine/live/student/autoMode"
import { playLiveSound } from "../../../../../../engine/live/student/playLiveSound"
import { useAutoMode } from "../../../../../../engine/live/student/useAutoMode"
import { useGeneratedActivity } from "../../../../../../engine/precall/checkpoint/useGeneratedActivity"
import { sharedCourseOf } from "../../../../../../engine/live/student/liveChatChannels"
import { STUDENT_TAP_TRIGGER } from "../../../../../../engine/live/student/liveCheckpoint"
import { rosterTutorIdsOf } from "../../../../../../engine/live/student/liveTutorCast"
import { hasFeature } from "../../../../../../data/utility/Utils"
import { shouldAskRating, sessionStartOf } from "../../../../../../engine/live/student/liveSessionTime"
import { useStudentLive } from "../../../../../../engine/live/student/useStudentLive"
import { useCorrectionWatch } from "../../../../../../engine/activity/correction/useCorrectionWatch"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { toUIActivities } from "../../../../adapters/parent/Activities/activityAdapter"
import { findStudentActivity } from "../../../../adapters/student/Activities/studentActivitiesAdapter"
import { type ApiFinishedActivity } from "../../../../adapters/student/Activity/finishedActivityAdapter"
import { toUITutor } from "../../../../adapters/parent/LiveSessions/liveSessionListAdapter"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import { type ApiRunActivity } from "../../../../adapters/student/Activity/studentActivityAdapter"
import {
    avatarOf,
    findLiveSession,
    liveActivityPlace,
    liveConnectedStudents,
    liveChatChannelOptions,
    liveOpenActivity,
    liveTodos,
    liveUnreadCount,
    pickLiveActivity,
    principalTutorOf,
    toLiveHeading,
    toLiveMe,
    toLiveTutor,
    toLiveTutorRounds,
    toUILiveTutor,
    type ApiLiveAccount,
    type LiveActivityPlace,
    type LiveRoomMember,
} from "../../../../adapters/student/Live/studentLiveAdapter"
import {
    detectedProblem,
    liveAutoModeSheet,
    liveChatDays,
    liveChatTyping,
    liveSoundMode,
    liveWaiting,
    replacementSession,
} from "../../../../view-models/student/Live/studentLiveViewModel"
import { studentActivitiesViewModel } from "../../../../view-models/student/Activities/studentActivitiesViewModel"
import { realizationsViewModel } from "../../../../view-models/parent/Activities/realizationsViewModel"
import { useRealizationsData } from "../../parent/Activities/useRealizationsData"
import { selectAccountInFlight } from "../useAccountRereadOnReturn"
import { type UILiveProblem, type UILiveTwin } from "../../../types/studentLive"
import { type StudentActivityLiveInputs, useStudentActivityView } from "../Activity/useStudentActivityView"
import { type StudentActivityOutcome } from "../Activity/StudentActivityBody"
import { LIVE_SOUNDS, type LiveScreenSound } from "./liveSounds"
import { useLiveVideoTracks } from "./liveTracks"
import { initialLiveDevicesState, liveDevicesReducer } from "../../../../view-models/student/Live/liveDevicesViewModel"
import { messengerVoiceOutbox } from "../Messaging/messengerVoiceOutbox"
import { useLiveAppBanner } from "./useLiveAppBanner"
import { useLiveChatAttachments } from "./useLiveChatAttachments"
import { useLiveClosing, type LiveClosing } from "./useLiveClosing"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type RootState = {
    data?: {
        currentUser?: { object?: ApiLiveAccount | null }
        staticData?: { school_subjects?: ApiSchoolSubject[] }
        currentActivity?: { activity?: { activityData?: ApiRunActivity | null } | null }
    }
    liveMode?: { suggestedActivities?: Record<string, number | null> }
}

/** Le partage d'écran de l'ancienne barre ne se montrait qu'au-delà de cette largeur de fenêtre. */
const SCREEN_SHARE_FROM = 700

export function useStudentLiveScreen() {
    const dispatch = useDispatch()
    const telemetryRef = useRef<ReturnType<typeof useStudentLive>["telemetry"] | null>(null)
    const closingRef = useRef<LiveClosing | null>(null)
    const quizConversationRef = useRef<number | null>(null)

    const play = useCallback((sound: LiveScreenSound) => {
        if (telemetryRef.current != null) playLiveSound(LIVE_SOUNDS[sound], telemetryRef.current)
    }, [])

    const permissions = useMediaPermissions()
    const live = useStudentLive({
        voiceOutbox: messengerVoiceOutbox,
        micPermissionDenied: permissions.microphone === false,
        onCue: play,
        onEnded: (exit) => closingRef.current?.onEnded(exit),
        onReopened: () => closingRef.current?.onReopened(),
        onOut: (exit) => closingRef.current?.onOut(exit),
        onActivityPushed: () => {
            // Le quiz en cours se dit interrompu par le tuteur, comme l'ancien écran.
            const conversationId = quizConversationRef.current
            if (conversationId == null) return
            apiCall(dispatch as never, liveService.finishConversation, {
                routeParams: { conversation_id: conversationId },
                data: { interruption_reason: "tutor_interruption", ended_at: new Date().toISOString() },
            }).catch((cause: unknown) => {
                telemetryRef.current?.error("quiz_interrupt", cause)
            })
        },
    })
    telemetryRef.current = live.telemetry
    // La séance et l'activité en pause passent aux écrans qui suivent : la sortie du live les efface du magasin.
    const closing = useLiveClosing(live.telemetry, {
        liveSessionId: live.liveSessionId,
        activityPaused: () => activityRef.current != null,
    })
    closingRef.current = closing

    const account = useSelector((state: RootState) => selectDisplayedStudent(state))
    const subjects = useSelector((state: RootState) => state.data?.staticData?.school_subjects)
    const stored = useSelector((state: RootState) => state.data?.currentActivity?.activity?.activityData ?? null)
    const suggestedId = useSelector((state: RootState) =>
        live.liveSessionId != null ? (state.liveMode?.suggestedActivities?.[String(live.liveSessionId)] ?? null) : null
    )
    const session = findLiveSession(account, live.liveSessionId)
    const tutorIds = useMemo(() => rosterTutorIdsOf(session), [session])
    const groupSession = supportsNoShowRecovery(live.liveSession?.live_room) === true
    const roster = session?.users ?? []
    const sharedCourse = sharedCourseOf(roster)
    // Le principal (le remplaçant du jour, sinon le tuteur de la séance) : la scène, l'en-tête, le chat.
    const principal = principalTutorOf(session, live.tutors)
    // Le tuteur du fil ouvert : celui de sa puce, ou le principal dans « Tous ».
    const chatTutor =
        roster.find((member) => member?.is_tutor === true && String(member.id) === live.chat.channel) ?? principal

    /* ── l'activité ouverte ── */
    const todos = useMemo(() => liveTodos(account), [account])
    const finishedRef = useRef<Set<number>>(new Set())
    // Au rechargement, l'activité rangée (`data.currentActivity`) reprend sa place, comme aujourd'hui.
    const restoredRef = useRef<number | null>(typeof stored?.id === "number" ? stored.id : null)
    const activityId = live.state.activityId
    useEffect(() => {
        if (activityId != null) return
        const candidates = todos.filter((todo) => todo.id != null && !finishedRef.current.has(todo.id))
        const restored = restoredRef.current
        restoredRef.current = null
        const next =
            restored != null && candidates.some((todo) => todo.id === restored)
                ? restored
                : pickLiveActivity(candidates, suggestedId)
        if (next != null) live.actions.selectActivity(next)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, todos, suggestedId])

    // L'activité promise par Sam au démarrage (« quiz d'échauffement ») : poussée avant l'entrée dans
    // la salle, son `newTodo` s'est perdu ; elle s'ouvre dès que la liste relue la porte, reconnue par
    // son id (celui que le serveur a rattaché à la conversation), jamais comme « la prochaine qui arrive ».
    const activityIdRef = useRef(activityId)
    activityIdRef.current = activityId
    useGeneratedActivity({
        liveSessionId: live.liveSessionId,
        todos: todos as never,
        onFound: (generated) => {
            if (generated !== activityIdRef.current) live.actions.selectActivity(generated)
        },
        telemetry: live.telemetry,
    })

    const activity = useMemo<ApiRunActivity | null>(
        () =>
            liveOpenActivity<ApiRunActivity>({
                activityId,
                stored,
                todos: todos as ApiRunActivity[],
                pushed: live.pushedActivity as ApiRunActivity | null,
            }),
        [activityId, todos, live.pushedActivity, stored]
    )

    // `data.currentActivity` et « une activité en cours » suivent ce qui est ouvert.
    useEffect(() => {
        dispatch(
            setCurrentActivity(activity != null ? { activityData: activity, progressStatus: "ongoing" } : null) as never
        )
        dispatch(setOngoingActivity(activity != null) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity?.id])
    useEffect(
        () => () => {
            dispatch(setCurrentActivity(null) as never)
            dispatch(setOngoingActivity(false) as never)
        },
        [dispatch]
    )
    // Un écran poussé par-dessus le live a pu prendre la place de l'activité
    // courante : au retour, celle du live la reprend.
    const activityRef = useRef(activity)
    activityRef.current = activity
    useFocusEffect(
        useCallback(() => {
            const open = activityRef.current
            if (open == null) return
            dispatch(setCurrentActivity({ activityData: open, progressStatus: "ongoing" }) as never)
            dispatch(setOngoingActivity(true) as never)
        }, [dispatch])
    )

    /* ── les entrées du live de l'activité ── */
    const { send, setAttributes, telemetry, liveSessionId, user, dialogue } = live
    // « Refaire l'activité » sur une activité finie, dans le volet : l'activité refaite s'ouvre, et le
    // tuteur relit la liste de l'élève sur le `newTodo`, à toute la salle. L'attribut `activity_id` suit à
    // l'ouverture.
    const selectActivity = live.actions.selectActivity
    const openReplayed = useCallback(
        (fresh: { id?: number | null } & Record<string, unknown>) => {
            void send("newTodo", { id: user?.id, activity: fresh }, null, "keep")
            if (typeof fresh.id === "number") selectActivity(fresh.id)
        },
        [send, user?.id, selectActivity]
    )
    // La correction arrivée ailleurs (Wajih, 20/10) : une attente quittée avant elle se surveille, et
    // l'avis se pose, une fois, sans couper l'activité en cours. Le temps du live seulement : l'écran de
    // fin s'empile sur celui-ci sans le démonter.
    const corrections = useCorrectionWatch(telemetry, {
        enabled: live.state.ended == null && !live.leave.leaving,
    })
    const correctionLeft = corrections.watch
    const paused = live.activityPaused || (groupSession && dialogue)
    // L'activité retirée par le tuteur sans que `deleteTodo` arrive (l'élève hors de la salle à ce moment,
    // l'envoi perdu) : la liste relue fait foi, et la ferme comme `deleteTodo`, sans attendre le relevé du
    // chrono, arrêté en Dialogue. Le tuteur ne retire qu'une activité à faire : seule se ferme celle que la
    // liste montrait à faire, et qu'elle ne porte plus (`liveListFreshness` écarte une réponse partie avant
    // elle). Une activité pas encore listée (poussée par le tuteur), ou faite, ne ferme rien ; la fermeture
    // ne vaut que pour l'ouverture en cours (live du 31/10).
    const seenRef = useRef<{ activityId: number; place: LiveActivityPlace } | null>(null)
    const [droppedId, setDroppedId] = useState<number | null>(null)
    useEffect(() => {
        if (droppedId != null && droppedId !== activityId) setDroppedId(null)
        if (activityId == null) return
        const place = liveActivityPlace(account, activityId)
        if (place == null) return
        const seen = seenRef.current?.activityId === activityId ? seenRef.current.place : null
        if (place === seen) return
        telemetry.transition(seen ?? "unseen", place, "list_reread", { activityId })
        seenRef.current = { activityId, place }
        if (place !== "gone" || seen !== "todo") return
        setDroppedId(activityId)
        // Le signal à suivre : une activité retirée dont `deleteTodo` n'est pas arrivé.
        if (live.removedActivityId !== activityId) {
            telemetry.error("activity_dropped_by_list", new Error("open activity no longer listed"), {
                activityId,
                dialogue,
                paused,
            })
        }
        // Jugée à chaque liste écrite du compte, et à chaque activité ouverte.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, account?.todo_activities, account?.activities])
    const removed = activityId != null && (live.removedActivityId === activityId || droppedId === activityId)
    const finalizedByTutor = activityId != null && live.tutorFinalizedId === activityId
    const liveInputs = useMemo<StudentActivityLiveInputs>(
        () => ({
            liveSessionId,
            removed,
            finalizedByTutor,
            openReplayed,
            correctionLeft,
            dialogue,
            paused,
            announceWorkUpdated: (info) => {
                void send("studentWorkUpdated", { studentId: user?.id, ...info }, null, "keep")
            },
            announceFinalized: (finalized) => {
                void send(
                    "activityFinalized",
                    { displayName: user?.display_name, activityId: finalized.id },
                    tutorIds.length > 0 ? tutorIds.map(Number) : null,
                    "keep"
                )
            },
            setVoiceRecording: (enabled) => {
                if (liveSessionId == null) return
                apiCall(dispatch as never, liveServiceV2.setVoiceRecordingActivityV2, {
                    routeParams: { id: liveSessionId },
                    data: { enabled },
                }).catch((cause: unknown) => {
                    telemetry.error("voice_recording", cause)
                })
            },
            setActivityAttribute: (id) => {
                setAttributes({ activity_id: id != null ? String(id) : "" }).catch((cause: unknown) => {
                    telemetry.error("activity_attribute", cause)
                })
            },
        }),
        [
            liveSessionId,
            removed,
            finalizedByTutor,
            openReplayed,
            correctionLeft,
            paused,
            dialogue,
            send,
            user?.id,
            user?.display_name,
            tutorIds,
            dispatch,
            telemetry,
            setAttributes,
        ]
    )

    const onPrerequisiteQuizCompleted = useCallback(
        (result: ApiCheckpointConversation) => {
            // Le tuteur reçoit le bilan du quiz, comme à la fin d'un checkpoint (`useLiveKitSync.notifyTutor`).
            void send(
                "agentConversationCompleted",
                {
                    liveSessionId,
                    studentId: user?.id,
                    tutor_id: session?.tutor?.id,
                    displayName: user?.display_name,
                    conversation: result,
                    timestamp: Date.now(),
                },
                null,
                "keep"
            )
        },
        [send, liveSessionId, user?.id, user?.display_name, session?.tutor?.id]
    )

    const activityView = useStudentActivityView({
        activity,
        live: liveInputs,
        heading: "inline",
        canGoBack: false,
        onPrerequisiteQuizCompleted,
    })

    // Le quiz de prérequis tient la place : Sam envoyé par le tuteur attend sa fin pour s'ouvrir, sans
    // voiler la visio (Wajih, 20/10).
    const quizBlocking = activityView.quiz.blocking === true
    const holdSam = live.actions.holdSam
    useEffect(() => holdSam(quizBlocking), [holdSam, quizBlocking])

    // Le quiz ouvert : `ongoing_checkpoint` le dit au tuteur, `checkpoint_context` lui dit que c'est le quiz et pas
    // le champion (il ne le montre jamais comme une conversation du champion), et sa conversation s'interrompt si le
    // tuteur pousse une autre activité.
    const quizOpen = activityView.quiz.state.status === "quiz"
    const quizConversationId = activityView.quiz.state.quiz?.conversation_id ?? null
    quizConversationRef.current = quizOpen ? quizConversationId : null
    useEffect(() => {
        if (!quizOpen) return undefined
        const name = user?.display_name ?? ""
        setAttributes({
            ongoing_checkpoint: "1",
            [CHECKPOINT_CONTEXT_ATTRIBUTE]: PREREQUISITE_QUIZ_CONTEXT,
            display_name: name,
        }).catch((cause: unknown) => {
            telemetry.error("quiz_attribute", cause)
        })
        return () => {
            setAttributes({ ongoing_checkpoint: "", [CHECKPOINT_CONTEXT_ATTRIBUTE]: "", display_name: name }).catch(
                (cause: unknown) => {
                    telemetry.error("quiz_attribute", cause)
                }
            )
        }
    }, [quizOpen, setAttributes, telemetry, user?.display_name])

    /* ── le mode autonome ── */
    const autoMode = useAutoMode({
        tutorIds: tutorIds.map(Number),
        roomHasQuiz: hasFeature(live.liveSession?.live_room, "prerequisite-quiz") === true,
        send,
        telemetry,
        onStarted: (id) => live.actions.selectActivity(id),
        onCue: play,
    })

    // Une activité finie (ou arrêtée) pendant ce live : la suivante n'est plus « la première » pour Sam.
    const [finishedSome, setFinishedSome] = useState(false)
    const onActivityFinished = useCallback(
        (outcome: StudentActivityOutcome) => {
            if (activityId != null && outcome.kind !== "left") {
                finishedRef.current.add(activityId)
                setFinishedSome(true)
            }
            if (outcome.kind === "stopped") {
                setActivitiesOpen(true)
                return
            }
            // Soumise en Dialogue : l'élève reste sur l'activité, elle se relit (Wajih, 25/10).
            if (outcome.kind === "submitted") return
            const next = activityView.nextActivity?.id ?? null
            // Plus rien à faire, en Concentré dans un cours de groupe : Sam prend le relais.
            if (
                outcome.kind === "done" &&
                autoModeOpens({
                    roomType: live.liveSession?.live_room?.type,
                    dialogue,
                    todoCount: next != null && next !== activityId ? 1 : 0,
                    source: activity as never,
                    studentId: account?.id,
                })
            ) {
                autoMode.open(activity as never)
            }
            live.actions.selectActivity(outcome.kind === "done" && next != null && next !== activityId ? next : null)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activityId, activityView.nextActivity?.id, activity, dialogue, account?.id, live.liveSession]
    )

    /* ── les feuilles ── */
    const [activitiesOpen, setActivitiesOpen] = useState(false)
    const [helpOpen, setHelpOpen] = useState(false)
    const [helpProblem, setHelpProblem] = useState<UILiveProblem | null>(null)
    const [confirmLeave, setConfirmLeave] = useState(false)
    const [reported, setReported] = useState(false)
    // Sam touché en Dialogue : il n'y est pas (Wajih, 20/10), l'écran dit pourquoi. Le Concentré revenu,
    // la phrase n'a plus lieu d'être.
    // Le Dialogue qui interrompt sa conversation (le moteur la ferme) le dit aussi (Wajih, daily du 21/10) :
    // la feuille ne se ferme plus sans un mot. Le panneau se lit au rendu d'avant : en Dialogue, le moteur
    // ne le montre déjà plus.
    const [samAway, setSamAway] = useState(false)
    // Sam touché demande d'abord (Wajih, 25/10) : un toucher de trop ouvrait une conversation, le plus souvent
    // refermée aussitôt. Rien ne s'ouvre sans le oui.
    const [samConfirm, setSamConfirm] = useState(false)
    const samBusy = live.checkpoint != null || live.checkpointOpening !== "idle"
    const samPanelBefore = useRef(false)
    useEffect(() => {
        setSamAway(dialogue && samPanelBefore.current)
    }, [dialogue])
    useEffect(() => {
        samPanelBefore.current = samBusy || samConfirm
    })
    /** La question se referme, et la trace dit comment : une conversation de Sam se lit de bout en bout. */
    const closeSamConfirm = useCallback(
        (outcome: "confirmed" | "declined" | "withdrawn") => {
            setSamConfirm(false)
            telemetry.transition("sam_confirm", outcome === "confirmed" ? "sam_opening" : "in_live", outcome)
        },
        [telemetry]
    )
    // Elle n'a plus lieu d'être quand Sam s'en va (le Dialogue, qui le dit), qu'il est déjà là (envoyé par le
    // tuteur) ou que le quiz de prérequis tient la place.
    useEffect(() => {
        if (samConfirm && (dialogue || samBusy || quizBlocking)) closeSamConfirm("withdrawn")
    }, [samConfirm, dialogue, samBusy, quizBlocking, closeSamConfirm])
    const openSam = () => {
        if (dialogue) {
            setSamAway(true)
            return
        }
        // Le quiz de prérequis tient la place : sa conversation ne se poserait pas, ne la crée pas.
        if (quizBlocking) return
        telemetry.transition("in_live", "sam_confirm", "student_tap")
        setSamConfirm(true)
    }
    const confirmSam = () => {
        closeSamConfirm("confirmed")
        // Sa conversation se range sous la source du geste de l'élève, pas sous celle du tuteur.
        live.actions.openCheckpoint(activityId, STUDENT_TAP_TRIGGER)
    }
    const cancelSam = () => closeSamConfirm("declined")

    const askLeave = () => setConfirmLeave(true)
    const confirmLeaving = () => {
        setConfirmLeave(false)
        if (shouldAskRating(sessionStartOf(live.liveSession))) live.leave.openRating()
        else live.leave.leave()
    }

    /* ── la scène ── */
    // Mes autres appareils (Wajih, daily du 21/10) : leur case dans la bande de ma vignette.
    const twins = useMemo<UILiveTwin[]>(
        () => live.otherDevices.map((device) => ({ id: device.identity, cameraOff: !device.cameraOn })),
        [live.otherDevices]
    )
    const twinIds = useMemo(() => live.otherDevices.map((device) => device.identity), [live.otherDevices])
    const tracks = useLiveVideoTracks(live.tutors, twinIds)
    const members: LiveRoomMember[] = live.room.participants.map((participant) => ({
        id: participant.id,
        isLocal: participant.isLocal,
        cameraOn: participant.cameraOn,
        micOn: participant.micOn,
    }))
    const { tutor, present } = toLiveTutor(session, live.tutors)
    const me = toLiveMe(account, {
        cameraOn: live.cameraOn,
        micOn: live.micOn,
        handRaised: live.state.handRaised.value,
    })
    const heading = toLiveHeading({ account, liveSessionId, subjects, principal })
    // La liste des activités ne change qu'avec la todo : le live, lui, se redessine souvent.
    const uiTodos = useMemo(() => toUIActivities(todos as never, subjects ?? []), [todos, subjects])
    // Les activités faites de l'onglet Activités : les mêmes, dans le même ordre (un sélecteur mémoïsé).
    const recentDone = useSelector(studentActivitiesViewModel).doneActivities
    // « Voir plus » : les activités faites suivantes, dans la feuille, page après page (les pages des
    // réalisations), au lieu de la route `realizations` et de l'ancien écran. Une touchée se relit dans
    // le volet, comme les autres : le live garde son activité, et la salle n'en sait rien.
    const [moreDone, setMoreDone] = useState(false)
    const doneHistory = useRealizationsData(null, undefined, { enabled: moreDone })
    const history = useSelector((state: unknown) => realizationsViewModel(state, null))
    const showHistory = moreDone && history.activities.length > 0 && !history.loading && !history.refreshing
    const doneActivities = showHistory ? history.activities : recentDone
    // La relecture d'une activité faite : relue dans le compte, comme l'onglet Activités. Une autre
    // activité ouverte (choisie, poussée par le tuteur) la referme.
    const [reviewId, setReviewId] = useState<string | number | null>(null)
    const review = useMemo(
        () => (reviewId != null ? (findStudentActivity(account, reviewId) as ApiFinishedActivity | null) : null),
        [account, reviewId]
    )
    // « Voir » sur l'avis de la correction : la relecture s'ouvre sur la copie corrigée.
    const [reviewWorkOpen, setReviewWorkOpen] = useState(false)
    useEffect(() => {
        if (reviewId == null) setReviewWorkOpen(false)
    }, [reviewId])
    // Une correction que l'élève regarde déjà (sa relecture, ou l'activité réaffichée) ne s'annonce pas.
    const { seen: correctionSeen } = corrections
    useEffect(() => {
        if (reviewId != null) correctionSeen(Number(reviewId))
    }, [reviewId, correctionSeen])
    useEffect(() => {
        if (activityId != null) correctionSeen(activityId)
    }, [activityId, correctionSeen])
    useEffect(() => setReviewId(null), [activityId])
    const newActivity = live.state.newActivity
    useEffect(() => {
        if (newActivity) setReviewId(null)
    }, [newActivity])

    // L'activité ouverte est-elle devant l'élève ? La page le dit, une activité faite relue prend sa place : le
    // tuteur, qui le croyait sur une activité cachée sous sa visio, ne pose l'avatar que là. `null` : la page
    // n'a encore rien dit, rien ne part.
    const [activityOnPage, setActivityOnPage] = useState<boolean | null>(null)
    const onScreenAttribute =
        activityOnPage == null ? null : activityOnPage && activity != null && review == null ? "1" : "0"
    useEffect(() => {
        if (onScreenAttribute == null) return
        setAttributes({ activity_on_screen: onScreenAttribute }).catch((cause: unknown) => {
            telemetry.error("activity_on_screen_attribute", cause)
        })
    }, [onScreenAttribute, setAttributes, telemetry])
    const reportActivityOnScreen = live.actions.reportActivityOnScreen
    const onActivityOnScreen = useCallback(
        (onScreen: boolean) => {
            setActivityOnPage(onScreen)
            reportActivityOnScreen(onScreen)
        },
        [reportActivityOnScreen]
    )

    // « Nouvelle activité de ton masteur » : dès que la liste relue porte l'activité ajoutée, qui s'ouvre alors.
    const addedId = live.state.addedActivityId
    const activityAdded = addedId != null && todos.some((todo) => todo.id === addedId)
    // « Mes activités » : relire la liste (le compte), « Voir plus ». Pas de « Générer » pendant le live.
    const refreshingTodos = useSelector(selectAccountInFlight)
    // La feuille refermée, elle se rouvre sur ses activités faites récentes.
    useEffect(() => {
        if (!activitiesOpen) setMoreDone(false)
    }, [activitiesOpen])
    const refreshTodos = useCallback(() => {
        dispatch(apiRequest(authService.getUser) as never)
    }, [dispatch])
    const appBanner = useLiveAppBanner()

    const { width } = useWindowDimensions()
    const screenShareToggle = useScreenShareToggle(live.telemetry)
    // Le choix des appareils en pleine séance : le web seulement, comme l'ancienne barre.
    const devices = useRoomDevices(live.telemetry)
    // Retourner ma caméra, depuis ma vignette : seulement quand une autre caméra est là.
    const cameraFlip = useCameraFlip(devices?.camera ?? null, live.telemetry)
    // Les bruits de fond, tenus le temps du live (la fenêtre des appareils ne fait que les montrer),
    // comme l'ancien sélecteur qui restait monté.
    const noiseFilter = useNoiseFilter(live.telemetry)
    const [devicesState, devicesDispatch] = useReducer(liveDevicesReducer, initialLiveDevicesState)
    // L'ancienne barre ne le montrait qu'au-delà de 700 px de fenêtre (`ControlBarM.web`). Une fenêtre qui
    // passe sous ce seuil, ou au-dessus, laisse son fil d'Ariane : le bouton disparu se lira dans la trace.
    const roomyForShare = width > SCREEN_SHARE_FROM
    const screenShare = roomyForShare ? screenShareToggle : null
    useEffect(() => {
        live.telemetry.transition(
            roomyForShare ? "narrow" : "roomy",
            roomyForShare ? "roomy" : "narrow",
            "screen_share_width",
            { width }
        )
        // La largeur au passage seulement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomyForShare, live.telemetry])
    // Le micro refusé en séance l'est aussi pour le point du bouton et « Un problème ? » : l'accès lu
    // peut rester à « prompt » après un refus de la page (WebKit, en recette).
    const micAllowed = live.micRefusal.refused ? false : permissions.microphone
    // La caméra de même : un refus vu en séance la dit refusée, même quand l'accès ne se lit pas (Firefox).
    const cameraAllowed = live.cameraBlocker != null ? false : permissions.camera
    const helpContext = {
        dialogue,
        micOn: live.micOn,
        cameraOn: live.cameraOn,
        poorConnection: live.poorConnection,
        web: Platform.OS === "web",
        micAllowed,
        micBlocker: live.micRefusal.blocker,
        micSilent: live.micSilent,
        cameraAllowed,
        cameraBlocker: live.cameraBlocker ?? undefined,
    }
    const problem = detectedProblem(helpContext)

    const chatDays = useMemo(
        () =>
            liveChatDays({
                messages: live.chat.messages,
                selfId: user?.id,
                channel: live.chat.channel,
                roster,
                apiUrl: API_URL as string,
                avatarOf,
            }),
        // `roster` vient du compte, relu avec lui.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [live.chat.messages, user?.id, live.chat.channel, session?.users]
    )

    // Le fil à plat : la visionneuse fait défiler toutes les photos du fil ouvert.
    const chatMessages = useMemo(
        () => chatDays.flatMap((day) => day.bursts.flatMap((burst) => burst.messages)),
        [chatDays]
    )
    const chatAttachments = useLiveChatAttachments(
        {
            threadId: live.chat.threadId,
            sendAttachment: live.chat.sendAttachment,
            sendVoice: live.chat.sendVoice,
            channel: live.chat.channel,
            setChannel: live.chat.setChannel,
        },
        chatMessages
    )

    const subjectName = (id: number | string | null | undefined, name: string | null | undefined) =>
        name ?? subjects?.find((subject) => String(subject?.id) === String(id))?.name ?? null

    /**
     * Une activité à faire choisie (« Mes activités », « Nouvelle activité de ton masteur ») : elle s'ouvre et la
     * relecture se ferme. Un choix de l'élève : au téléphone, la machine passe sur l'onglet Activité.
     */
    const selectTodo = (id: number) => {
        setActivitiesOpen(false)
        setReviewId(null)
        live.actions.selectActivity(id, true)
    }

    return {
        live,
        width,
        autoMode,
        autoModeSheet: liveAutoModeSheet(autoMode.state, subjectName),
        chatAttachments,
        chatTyping: liveChatTyping(live.chat.typingId, chatTutor),
        heading,
        soundMode: liveSoundMode(dialogue),
        tutor,
        tutorAbsent: !present,
        tutorRounds: toLiveTutorRounds(session, live.tutors),
        tutorUI: toUILiveTutor(principal),
        me,
        twins,
        connectedStudents: liveConnectedStudents(session, members, account?.id),
        tracks,
        waiting: liveWaiting(live.waiting.waiting),
        replacement: replacementSession(live.waiting.dialog, (raw) =>
            toUITutor({ id: raw?.id, display_name: raw?.display_name, avatar: avatarOf(raw?.avatar) })
        ),
        unreadCount: liveUnreadCount(session),
        problemDetected: problem != null,
        micBlocked: micAllowed === false,
        cameraBlocked: cameraAllowed === false,
        screenShare,
        devices,
        cameraFlip,
        noiseFilter,
        devicesState,
        devicesDispatch,
        micAllowed,
        chatChannels: liveChatChannelOptions(session),
        appBanner,
        sharedCourse,
        chatDays,
        todos: uiTodos,
        doneActivities,
        activityId,
        activity,
        finishedSome,
        activityView,
        onActivityFinished,
        openReplayed,
        review,
        /** La relecture s'ouvre sur la copie corrigée (« Voir » sur l'avis de la correction). */
        reviewWorkOpen,
        /** Une correction arrivée pendant que l'élève était ailleurs : l'avis à poser, une fois. */
        correctionReady: corrections.notice,
        /** Le message de clôture à poser, `null` sinon. */
        closedDialog: closing.dialog,
        /** « Nouvelle activité de ton masteur · Voir » : l'activité ajoutée sans être affichée, dans la liste. */
        activityAdded,
        /** La page dit si l'activité se voit : le tuteur le lit (`activity_on_screen`). */
        onActivityOnScreen,
        sheets: {
            activitiesOpen,
            setActivitiesOpen,
            selectTodo,
            /** « Voir » sur « Nouvelle activité de ton masteur » : elle s'ouvre comme un choix dans la liste. */
            showAddedActivity: () => {
                if (addedId != null) selectTodo(addedId)
            },
            /** Une activité faite touchée dans « Mes activités » : elle se relit dans le volet du live. */
            openDone: (id: string | number) => {
                setActivitiesOpen(false)
                setReviewId(id)
                setReviewWorkOpen(false)
                live.actions.changeView("activity")
            },
            /** « Voir » : la correction arrivée s'ouvre dans le volet, sur la copie corrigée ; l'avis ne revient pas. */
            openCorrection: (id: number) => {
                corrections.dismiss(id)
                setActivitiesOpen(false)
                setReviewId(id)
                setReviewWorkOpen(true)
                live.actions.changeView("activity")
            },
            /** L'avis fermé (la croix, ou son retrait) : il ne revient pas ; la correction reste dans l'activité finie. */
            dismissCorrection: corrections.dismiss,
            /** Le retour de la relecture : l'activité du live reprend sa place. */
            closeReview: () => setReviewId(null),
            refreshTodos,
            refreshingTodos,
            /** « Voir plus » : la feuille montre les activités faites suivantes ; plus rien après la dernière page. */
            seeAllDone: !moreDone
                ? () => setMoreDone(true)
                : showHistory && doneHistory.hasMore
                  ? doneHistory.loadMore
                  : undefined,
            helpOpen,
            helpProblem,
            openHelp: () => {
                setHelpProblem(problem)
                setHelpOpen(true)
            },
            closeHelp: () => setHelpOpen(false),
            helpContext,
            confirmLeave,
            askLeave,
            cancelLeave: () => setConfirmLeave(false),
            confirmLeaving,
            reported,
            setReported,
            /** Sam touché : « Tu veux parler à Sam ? », ou en Dialogue la phrase qui dit qu'il n'y est pas. */
            openSam,
            /** « Tu veux parler à Sam ? » est posée ; le oui ouvre sa conversation, le non ne fait rien. */
            samConfirm,
            confirmSam,
            cancelSam,
            samAway,
            dismissSamAway: () => setSamAway(false),
        },
    }
}

export type StudentLiveScreenModel = ReturnType<typeof useStudentLiveScreen>
